'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["label", "value", "onChange", "readOnly", "useOnDark", "helpTitle"];
import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import Toggle from './Toggle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.form.withInlineEditing.formFieldEditor.${key}`;
const FieldPropTextInput = props => {
  const {
      label,
      value,
      onChange,
      readOnly,
      useOnDark,
      helpTitle
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const labelToRender = useMemo(() => helpTitle ? /*#__PURE__*/_jsx(UIHelpIcon, {
    title: helpTitle,
    children: label
  }) : label, [helpTitle, label]);
  return /*#__PURE__*/_jsx(UIFormControl, Object.assign({
    label: labelToRender
  }, rest, {
    children: /*#__PURE__*/_jsx(UITextInput, {
      defaultValue: value,
      onChange: ({
        target: {
          value: newValue
        }
      }) => onChange(newValue),
      readOnly: readOnly,
      use: useOnDark ? 'on-dark' : 'default'
    })
  }));
};
const FormFieldEditor = ({
  field,
  useOnDark = false,
  openFormFn = () => {},
  onChange = () => {}
}) => {
  const isRequired = field.getIsRequired();
  const isHidden = field.getIsHidden();
  const options = field.getOptions();
  const hasDefaultValue = field.getHasDefaultValue();
  const hasPlaceholderValue = field.getHasPlaceholderValue();
  return /*#__PURE__*/_jsxs(UIForm, {
    "data-selenium": "form-field-editor",
    children: [/*#__PURE__*/_jsx(Toggle, {
      "data-selenium": "form-field-required",
      id: "form-field-required-toggle",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('fieldRequired')
      }),
      value: isRequired,
      onChange: () => onChange(field.setIsRequired(!isRequired))
    }), /*#__PURE__*/_jsx(Toggle, {
      "data-selenium": "form-field-hidden",
      id: "form-field-hidden-toggle",
      label: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('fieldHidden')
        }), /*#__PURE__*/_jsx(UITooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('hiddenToggleTooltip')
          }),
          placement: "right",
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "info",
            size: "small",
            className: "m-left-1"
          })
        })]
      }),
      value: isHidden,
      disabled: isRequired,
      disabledTooltipTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('hiddenFieldDisabled')
      }),
      onChange: () => onChange(field.setIsHidden(!isHidden))
    }), /*#__PURE__*/_jsx(FieldPropTextInput, {
      "data-selenium": "form-field-name",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('propertyName')
      }),
      value: field.getPropertyName(),
      readOnly: true,
      useOnDark: useOnDark
    }), /*#__PURE__*/_jsx(FieldPropTextInput, {
      "data-selenium": "form-field-label",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('fieldLabel')
      }),
      helpTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('fieldLabelHelp')
      }),
      value: field.getLabel(),
      onChange: value => onChange(field.setLabel(value)),
      useOnDark: useOnDark
    }), hasPlaceholderValue && /*#__PURE__*/_jsx(FieldPropTextInput, {
      "data-selenium": "form-field-placeholder",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('placeholder')
      }),
      helpTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('placeholderHelp')
      }),
      value: field.getPlaceholder(),
      onChange: value => onChange(field.setPlaceholder(value)),
      useOnDark: useOnDark
    }), hasDefaultValue && /*#__PURE__*/_jsx(FieldPropTextInput, {
      "data-selenium": "form-field-defaultvalue",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('defaultValue')
      }),
      helpTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('defaultValueHelp')
      }),
      value: field.getDefaultValue(),
      onChange: value => onChange(field.setDefaultValue(value)),
      useOnDark: useOnDark
    }), options.length > 0 && /*#__PURE__*/_jsx("div", {
      className: "m-top-2",
      "data-test-id": "go-to-full-editor",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('fieldValuesHelp_jsx'),
        options: {
          external: true,
          onClick: openFormFn
        },
        elements: {
          Link: UILink
        }
      })
    })]
  });
};
FormFieldEditor.propTypes = {
  field: PropTypes.object,
  useOnDark: PropTypes.bool,
  openFormFn: PropTypes.func,
  onChange: PropTypes.func
};
export default FormFieldEditor;